// Central de Treinamento (PO1-3505, épico PO1-3504): os tutoriais que o usuário
// pode ver, num lugar só e dentro do Hub. Vídeo embutido (YouTube não listado),
// manual no visualizador do Hub (PO1-3738), busca por texto, chips por produto (só no
// Everlog Admin, que vê tudo) e selo "Novo" (publicado há até 30 dias).
//
// `produto` vem do item de menu (componenteComProduto em routes/products.js) e
// vai na consulta: o servidor filtra por produto e permissão, igual às telas.
// `?tutorial=<codigo>` abre direto o modal do vídeo: é o link que o Autopilot
// e o painel "Como usar esta tela" devolvem.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Modal, ModalBody, Spinner } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard from "../../../components/ShellCard";
import ChatModal from "../../../components/autopilot/ChatModal";
import { AUTOPILOT_COLORS, autopilotInsetCardStyle } from "../../../components/autopilot/styles";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import { listarTutoriais, mensagemErroTreinamento } from "../../../services/treinamentoApi";
import FalarComSuporte, { ContatoSuporte, temAutopilot } from "../FalarComSuporte";
import ManualModal from "../ManualModal";
import Player from "../Player";
import { PRODUTOS_TUTORIAL, PRODUTO_ROTULO, formatarDuracao, miniaturaYoutube } from "../rotas";

import "./Central.css";

function normalizar(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// Busca por título, resumo, capítulos e passos.
export function filtrarTutoriais(tutoriais, busca, produto) {
  const q = normalizar(busca);
  return (Array.isArray(tutoriais) ? tutoriais : []).filter((t) => {
    if (produto && (t.produtos || []).indexOf(produto) === -1) return false;
    if (!q) return true;
    const indice = normalizar(
      [t.titulo, t.resumo]
        .concat((t.capitulos || []).map((c) => c && c.titulo))
        .concat(t.passos || [])
        .filter(Boolean)
        .join(" "),
    );
    return indice.indexOf(q) !== -1;
  });
}

export function tutorialDaBusca(search) {
  const q = new URLSearchParams(search || "");
  const codigo = q.get("tutorial");
  return codigo ? String(codigo).trim() : null;
}

function SeloNovo() {
  return (
    <span
      className="tutorial-card__novo"
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.72rem",
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 10,
        background: AUTOPILOT_COLORS.statusSuccessBg,
        color: AUTOPILOT_COLORS.statusSuccess,
        border: `1px solid ${AUTOPILOT_COLORS.statusSuccessBorder}`,
      }}
    >
      Novo
    </span>
  );
}

const cardStyle = {
  ...autopilotInsetCardStyle,
  padding: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const corpoStyle = {
  padding: "0.8rem 1rem 0.9rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  flex: 1,
  minWidth: 0,
};

const botaoStyle = {
  background: "transparent",
  border: "1px solid var(--theme-accent-border)",
  color: AUTOPILOT_COLORS.accent,
  fontWeight: 600,
  boxShadow: "none",
  whiteSpace: "nowrap",
  padding: "0.4rem 0.6rem",
};

export function TutorialCard({ tutorial, onAssistir, onManual, onPerguntar, mostrarProduto }) {
  const miniatura = miniaturaYoutube(tutorial.youtubeId);
  const duracao = formatarDuracao(tutorial.duracaoSegundos);
  return (
    <div className="tutorial-card" style={cardStyle} data-testid={`tutorial-${tutorial.codigo}`}>
      <div
        className="tutorial-card__capa"
        role={tutorial.youtubeId ? "button" : undefined}
        tabIndex={tutorial.youtubeId ? 0 : undefined}
        onClick={tutorial.youtubeId ? () => onAssistir(tutorial) : undefined}
        onKeyDown={
          tutorial.youtubeId
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAssistir(tutorial);
                }
              }
            : undefined
        }
        style={tutorial.youtubeId ? { cursor: "pointer" } : undefined}
      >
        {miniatura ? (
          <img src={miniatura} alt="" loading="lazy" />
        ) : (
          <div className="tutorial-card__capa-vazia">
            <i className="tim-icons icon-paper" />
          </div>
        )}
        {tutorial.novo ? <SeloNovo /> : null}
        {duracao ? <span className="tutorial-card__duracao">{duracao}</span> : null}
      </div>
      <div style={corpoStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.3 }}>{tutorial.titulo}</div>
          {mostrarProduto ? (
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", marginTop: 2 }}>
              {(tutorial.produtos || []).map((p) => PRODUTO_ROTULO[p] || p).join(" · ")}
            </div>
          ) : null}
        </div>
        {tutorial.resumo ? (
          <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.8rem", lineHeight: 1.5 }}>{tutorial.resumo}</div>
        ) : null}
        <div className="tutorial-card__acoes">
          {tutorial.youtubeId ? (
            <Button type="button" size="sm" style={botaoStyle} onClick={() => onAssistir(tutorial)} title={`Assistir ${tutorial.titulo}`}>
              <i className="tim-icons icon-triangle-right-17" style={{ marginRight: 4 }} />
              Assistir
            </Button>
          ) : null}
          {tutorial.temManual ? (
            <Button
              type="button"
              size="sm"
              style={botaoStyle}
              onClick={() => onManual(tutorial)}
              title="Abrir o manual"
            >
              <i className="tim-icons icon-paper" style={{ marginRight: 4 }} />
              Manual
            </Button>
          ) : null}
          {onPerguntar ? (
            <Button type="button" size="sm" style={botaoStyle} onClick={() => onPerguntar(tutorial)} title="Tirar dúvida com o Autopilot">
              <i className="tim-icons icon-chat-33" style={{ marginRight: 4 }} />
              Perguntar ao Autopilot
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const stateBoxStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.86rem",
  padding: "2rem 1rem",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.6rem",
};

export default function Central({ produto }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();

  const [tutoriais, setTutoriais] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [chip, setChip] = useState("");
  const [assistindo, setAssistindo] = useState(null);
  const [manual, setManual] = useState(null);
  const [perguntando, setPerguntando] = useState(null);
  const montado = useRef(true);

  const ehAdmin = produto === "EVERLOG_ADMIN";
  const comAutopilot = temAutopilot(user);
  const telaAtual = location.pathname + location.search;

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const lista = await listarTutoriais(produto);
      if (!montado.current) return;
      setTutoriais(lista);
    } catch (e) {
      if (!montado.current) return;
      const mensagem = mensagemErroTreinamento(e, "Não foi possível carregar os tutoriais.");
      setErro(mensagem);
      addToast({ place: "tr", type: "danger", title: "Erro", description: mensagem });
    } finally {
      if (montado.current) setCarregando(false);
    }
  }, [produto, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // `?tutorial=<codigo>`: abre o vídeo assim que a lista chega.
  const codigoDaUrl = tutorialDaBusca(location.search);
  useEffect(() => {
    if (!codigoDaUrl || tutoriais.length === 0) return;
    const alvo = tutoriais.find((t) => t.codigo === codigoDaUrl);
    if (alvo) setAssistindo({ tutorial: alvo, inicio: 0 });
  }, [codigoDaUrl, tutoriais]);

  const fecharVideo = useCallback(() => {
    setAssistindo(null);
    if (tutorialDaBusca(location.search)) {
      const q = new URLSearchParams(location.search);
      q.delete("tutorial");
      const s = q.toString();
      history.replace({ pathname: location.pathname, search: s ? `?${s}` : "" });
    }
  }, [history, location.pathname, location.search]);

  // Manual no visualizador do Hub (PO1-3738), por cima do vídeo se ele estiver aberto.
  const abrirManual = useCallback((tutorial) => {
    setAssistindo(null);
    setManual(tutorial);
  }, []);

  const contagemPorProduto = useMemo(() => {
    const mapa = {};
    tutoriais.forEach((t) => (t.produtos || []).forEach((p) => {
      mapa[p] = (mapa[p] || 0) + 1;
    }));
    return mapa;
  }, [tutoriais]);

  const filtrados = useMemo(() => filtrarTutoriais(tutoriais, busca, ehAdmin ? chip : ""), [tutoriais, busca, ehAdmin, chip]);

  const renderEstado = () => {
    if (carregando && tutoriais.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
          Carregando tutoriais…
        </div>
      );
    }
    if (erro && tutoriais.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700 }}>Não foi possível carregar</div>
          <p style={{ marginBottom: 0 }}>{erro}</p>
          <Button size="sm" color="info" outline onClick={carregar}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (filtrados.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700 }}>Nenhum tutorial encontrado</div>
          <p style={{ marginBottom: 0 }}>
            {busca || chip ? "Ajuste a busca ou limpe o filtro." : "Ainda não há tutorial publicado para o que você usa."}
          </p>
        </div>
      );
    }
    return null;
  };

  const estado = renderEstado();

  return (
    <div className="content treinamento-central">
      <PageHero
        eyebrow="Treinamento"
        title="Central de Treinamento"
        description={
          <>
            Vídeos e manuais de como usar o Everlog TMS, só com o que o seu perfil usa. O vídeo toca aqui mesmo e o manual abre em PDF.
            {comAutopilot ? " Ficou com dúvida? Pergunte ao Autopilot ou fale com o suporte." : ""}
          </>
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={carregando} />
            {comAutopilot ? <FalarComSuporte tela={telaAtual} /> : null}
          </>
        }
      >
        {!comAutopilot ? <ContatoSuporte /> : null}
      </PageHero>

      <ShellCard
        title="Tutoriais"
        description={`${filtrados.length} de ${tutoriais.length} tutorial(is)`}
        action={
          <Input
            type="search"
            bsSize="sm"
            aria-label="Buscar tutorial"
            placeholder="Buscar por título, assunto ou passo"
            value={busca}
            onChange={(e) => {
              const valor = e.target.value;
              setBusca(valor);
            }}
            style={{ minWidth: 240 }}
          />
        }
      >
        {ehAdmin && tutoriais.length > 0 ? (
          <div className="treinamento-chips" role="group" aria-label="Produto">
            <button type="button" className={`treinamento-chip${chip === "" ? " is-active" : ""}`} onClick={() => setChip("")}>
              Todos <span className="treinamento-chip__count">{tutoriais.length}</span>
            </button>
            {PRODUTOS_TUTORIAL.filter((p) => contagemPorProduto[p.codigo]).map((p) => (
              <button
                key={p.codigo}
                type="button"
                className={`treinamento-chip${chip === p.codigo ? " is-active" : ""}`}
                onClick={() => setChip(chip === p.codigo ? "" : p.codigo)}
              >
                {p.rotulo} <span className="treinamento-chip__count">{contagemPorProduto[p.codigo]}</span>
              </button>
            ))}
          </div>
        ) : null}

        {estado ? (
          estado
        ) : (
          <div className="treinamento-grid">
            {filtrados.map((t) => (
              <TutorialCard
                key={t.codigo}
                tutorial={t}
                mostrarProduto={ehAdmin}
                onAssistir={(tutorial) => setAssistindo({ tutorial, inicio: 0 })}
                onManual={abrirManual}
                onPerguntar={comAutopilot ? (tutorial) => setPerguntando(tutorial) : null}
              />
            ))}
          </div>
        )}
      </ShellCard>

      {/* Vídeo: modal no padrão do Hub (div de cabeçalho, sem ModalHeader; fade={false}). */}
      <Modal isOpen={!!assistindo} toggle={fecharVideo} size="lg" modalClassName="modal-align-top" fade={false}>
        {assistindo ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "var(--everlog-card-hero-bg)",
                borderBottom: "1px solid var(--everlog-card-border)",
                padding: "16px 20px",
              }}
            >
              <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>
                {assistindo.tutorial.titulo}
                {formatarDuracao(assistindo.tutorial.duracaoSegundos) ? (
                  <span style={{ color: AUTOPILOT_COLORS.muted, fontWeight: 500, marginLeft: 8 }}>
                    · {formatarDuracao(assistindo.tutorial.duracaoSegundos)}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={fecharVideo}
                style={{ background: "none", border: "none", color: "var(--everlog-card-title)", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
              <Player
                youtubeId={assistindo.tutorial.youtubeId}
                inicioSegundos={assistindo.inicio}
                capitulos={assistindo.tutorial.capitulos}
                titulo={assistindo.tutorial.titulo}
              />
              {assistindo.tutorial.resumo ? (
                <p style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.84rem", marginTop: "0.75rem", marginBottom: 0 }}>{assistindo.tutorial.resumo}</p>
              ) : null}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.9rem" }}>
                {assistindo.tutorial.temManual ? (
                  <Button type="button" size="sm" style={botaoStyle} onClick={() => abrirManual(assistindo.tutorial)}>
                    <i className="tim-icons icon-paper" style={{ marginRight: 4 }} />
                    Manual
                  </Button>
                ) : null}
                {comAutopilot ? (
                  <Button
                    type="button"
                    size="sm"
                    style={botaoStyle}
                    onClick={() => {
                      const t = assistindo.tutorial;
                      fecharVideo();
                      setPerguntando(t);
                    }}
                  >
                    <i className="tim-icons icon-chat-33" style={{ marginRight: 4 }} />
                    Perguntar ao Autopilot
                  </Button>
                ) : null}
              </div>
            </ModalBody>
          </>
        ) : null}
      </Modal>

      {manual ? <ManualModal isOpen tutorial={manual} onClose={() => setManual(null)} /> : null}

      {comAutopilot ? (
        <ChatModal
          isOpen={!!perguntando}
          onClose={() => setPerguntando(null)}
          title={perguntando ? `Autopilot · ${perguntando.titulo}` : "Autopilot"}
          contextoInicial={perguntando ? `Dúvida sobre o tutorial "${perguntando.titulo}" (código ${perguntando.codigo}). Tela: ${telaAtual}.` : null}
        />
      ) : null}
    </div>
  );
}
